import { AsyncFeedCurrency, AsyncInitCurrency, Currency, CurrencyNetworkEnum, FiatCurrency } from '../api/api';
import { cfgBaseCurrency, cfgBaseCurrencyFiat, cfgBaseCurrencySymbol } from '@infinitro/config';
import { get, writable } from 'svelte/store';
const CURRENCY_PRICE_FEED_INTERVAL = 5000;
export var RoundDirection;
(function (RoundDirection) {
    RoundDirection["up"] = "up";
    RoundDirection["down"] = "down";
    RoundDirection["nearest"] = "nearest";
})(RoundDirection || (RoundDirection = {}));
export const baseCurrencySymbol = cfgBaseCurrencySymbol();
export const baseCurrency = cfgBaseCurrency().toLowerCase();
export const baseCurrencyFiat = cfgBaseCurrencyFiat().toUpperCase();
export const defaultDecimalDisplayLength = 2;
export const staticCurrencyInfo = [
    {
        currency: Currency.usd,
        display: 2,
        fiatSymbol: '$',
        name: 'USD'
    },
    {
        display: 7,
        name: 'BTC',
        currency: Currency.btc
    },
    {
        display: 6,
        name: 'ETH',
        currency: Currency.eth
    },
    {
        display: 2,
        name: 'USDT',
        currency: Currency.usdt
    }
];
export const baseCurrencyDisplayLength = staticCurrencyInfo.find((c) => c.currency === baseCurrency)?.display ?? 2;
let _stateCurrencyInfo = staticCurrencyInfo;
export const stateCurrencyInfo = writable(staticCurrencyInfo);
stateCurrencyInfo.subscribe((info) => {
    _stateCurrencyInfo = info;
});
export const currencySymbol = (currency) => {
    return _stateCurrencyInfo.find((c) => c.currency == currency)?.fiatSymbol || '';
};
export const decimalDisplayLength = (currency) => {
    return (_stateCurrencyInfo.find((c) => c.currency == currency)?.display || defaultDecimalDisplayLength);
};
// displays the string amount with the correct number of decimal places for the given currency
export const formatStringAmountCurrency = (amount, currency) => {
    try {
        if (!amount)
            return '';
        const amountParts = amount.split('.');
        const digits = decimalDisplayLength(currency);
        if (amountParts.length === 1) {
            return amount + '.' + '0'.repeat(digits);
        }
        if (amountParts[1].length >= digits) {
            return amountParts[0] + '.' + amountParts[1].substring(0, digits);
        }
        return amountParts[0] + '.' + amountParts[1] + '0'.repeat(digits - amountParts[1].length);
    }
    catch (error) {
        console.warn('Error formatting currency string', error);
        return '';
    }
};
// formats an amount of the given currency to its correct display precision
export const formatCurrencyString = (amount, currency) => {
    try {
        amount = Number(amount);
        if (isNaN(amount))
            return '';
        const digits = decimalDisplayLength(currency);
        return amount.toFixed(digits);
    }
    catch (error) {
        console.warn('Error formatting currency string', error);
        return '';
    }
};
// formats an amount of the given currency to its correct display precision
export const formatCurrencyNumber = (amount, currency) => {
    return Number(formatCurrencyString(amount, currency));
};
export const formatBaseCurrencyNumber = (amount) => formatCurrencyNumber(amount, baseCurrency);
export const formatBaseCurrencyNumberString = (amount) => formatCurrencyString(amount, baseCurrency);
export const formatBaseCurrencyString = (amount) => `${baseCurrencySymbol}${formatBaseCurrencyNumber(amount)}`;
export const formatBaseWithCurrency = (amount) => `${formatBaseCurrencyNumber(amount)} ${baseCurrency.toUpperCase()}`;
export const currencyPrices = writable([]);
export const currencyInfo = writable([]);
export const isFiat = (currency) => {
    if (!currency)
        return false;
    return Object.keys(FiatCurrency).includes(currency?.toUpperCase());
};
export const isCrypto = (currency) => {
    if (!currency)
        return false;
    return (Object.keys(Currency).includes(currency?.toLowerCase()) &&
        !Object.keys(FiatCurrency).includes(currency?.toUpperCase()));
};
export const isFiatNetwork = (network) => {
    if (!network)
        return false;
    return [
        CurrencyNetworkEnum.cards,
        CurrencyNetworkEnum.fiat,
        CurrencyNetworkEnum.cash,
        CurrencyNetworkEnum.interac,
        CurrencyNetworkEnum.manual,
        CurrencyNetworkEnum.other
    ].includes(network);
};
export const isCryptoNetwork = (network) => {
    if (!network)
        return false;
    return ![
        CurrencyNetworkEnum.cards,
        CurrencyNetworkEnum.fiat,
        CurrencyNetworkEnum.cash,
        CurrencyNetworkEnum.interac,
        CurrencyNetworkEnum.manual,
        CurrencyNetworkEnum.other
    ].includes(network);
};
export const getCurrencyDisplayDecimals = (currency) => {
    return _stateCurrencyInfo.find((token) => token.currency == currency)?.display || 2;
};
/** Keeps partial numeric input (e.g. "1.") while capping fractional digits. */
export const limitNumericInputDecimals = (value, maxDecimals) => {
    if (!value)
        return value;
    const cleaned = value.replace(/[^0-9.]/g, '');
    const dotIndex = cleaned.indexOf('.');
    if (dotIndex === -1)
        return cleaned;
    const beforeDot = cleaned.slice(0, dotIndex);
    let afterDot = cleaned.slice(dotIndex + 1).replace(/\./g, '');
    if (maxDecimals === 0)
        return beforeDot || '0';
    if (afterDot.length > maxDecimals) {
        afterDot = afterDot.slice(0, maxDecimals);
    }
    return `${beforeDot}.${afterDot}`;
};
let _cryptoLoading = false;
export const cryptoError = writable(false);
export async function fetchAndSetCryptoPrices(retries = 3, delay = 1000) {
    if (_cryptoLoading) {
        console.log('Already fetching crypto prices.');
        return;
    }
    cryptoError.set(false);
    try {
        await fetchAndSetCryptoPricesWithRetry(retries, delay);
    }
    catch (error) {
        console.log('Error fetching crypto prices', JSON.stringify(error, null, 2));
        cryptoError.set(true);
    }
    finally {
        _cryptoLoading = false;
    }
}
let _initalizedCurrencies = false;
async function fetchAndSetCryptoPricesWithRetry(retries, delay) {
    try {
        if (_initalizedCurrencies) {
            const response = await AsyncFeedCurrency({});
            currencyPrices.set(response?.data?.currencyPrices);
            return response;
        }
        else {
            const response = await AsyncInitCurrency({});
            const currencies = response?.data?.listCurrencies;
            currencyInfo.set(currencies);
            stateCurrencyInfo.set(currencies);
            currencyPrices.set(response?.data?.currencyPrices);
            _initalizedCurrencies = true;
            return response;
        }
    }
    catch (error) {
        if (retries === 0) {
            throw error;
        }
        console.log(`Retrying fetching crypto prices in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return fetchAndSetCryptoPricesWithRetry(retries - 1, delay);
    }
}
let timer;
export const startCurrencyPriceFeed = () => {
    if (!timer) {
        timer = setInterval(() => {
            fetchAndSetCryptoPrices();
        }, CURRENCY_PRICE_FEED_INTERVAL);
    }
};
export const usdCurrencies = [Currency.usd, Currency.usdt, Currency.usdc];
export const isBaseCurrencyPegged = (currency) => {
    if (currency === baseCurrency)
        return true;
    if (currency === baseCurrencyFiat.toLowerCase())
        return true;
    if (usdCurrencies.includes(baseCurrency) && usdCurrencies.includes(currency))
        return true;
    return false;
};
export const forexPrice = (currency) => {
    if (isBaseCurrencyPegged(currency))
        return 1;
    return get(currencyPrices)?.find((price) => price.currency === currency)?.price;
};
export const forexRate = (from, to) => {
    if (to === from)
        return 1;
    if (usdCurrencies.includes(to) && usdCurrencies.includes(from))
        return 1;
    const toPrice = forexPrice(to);
    const fromPrice = forexPrice(from);
    if (!toPrice || !fromPrice)
        return undefined;
    return toPrice / fromPrice;
};
// note: assumption FE and BE must have same base currency, feed is returned in BE base currency
export const forexToBaseCurrency = (amount, currency) => {
    if (isNaN(amount))
        return undefined;
    const price = forexPrice(currency);
    if (!price)
        return undefined;
    if (price == 1)
        return formatCurrencyNumber(amount, baseCurrency);
    return formatCurrencyNumber(amount * price, baseCurrency);
};
export const forexToBaseCurrencyStr = (amount, currency) => {
    try {
        amount = Number(amount);
        if (isNaN(amount))
            return '';
        const price = forexPrice(currency);
        if (price == 1)
            return formatCurrencyString(amount, baseCurrency);
        if (!price)
            return undefined;
        return formatCurrencyString(amount / price, baseCurrency);
    }
    catch (error) {
        console.warn('Error formatting base currency string', error);
        return '';
    }
};
export const forexFromBaseCurrency = (amount, currency) => {
    try {
        amount = Number(amount);
        if (isNaN(amount))
            return undefined;
        const price = forexPrice(currency);
        if (!price)
            return undefined;
        if (price == 1)
            return formatCurrencyNumber(amount, currency);
        return formatCurrencyNumber(amount / price, currency);
    }
    catch (error) {
        console.warn('Error formatting base currency string', error);
        return undefined;
    }
};
export const forexFromBaseCurrencyStr = (amount, currency) => {
    try {
        amount = Number(amount);
        if (isNaN(amount))
            return '';
        const price = forexPrice(currency);
        if (price == 1)
            return formatCurrencyString(amount, currency);
        if (!price)
            return undefined;
        return formatCurrencyString(amount / price, currency);
    }
    catch (error) {
        console.warn('Error formatting base currency string', error);
        return '';
    }
};
export const forexToCurrency = (amount, from, to) => {
    try {
        amount = Number(amount);
        if (isNaN(amount))
            return undefined;
        if (isBaseCurrencyPegged(to))
            return forexToBaseCurrency(amount, from);
        if (isBaseCurrencyPegged(from))
            return forexFromBaseCurrency(amount, to);
        const price = forexRate(from, to);
        if (!price)
            return undefined;
        return formatCurrencyNumber(amount * price, to);
    }
    catch (error) {
        console.warn('Error formatting base currency string', error);
        return undefined;
    }
};
export const forexToCurrencyStr = (amount, from, to) => {
    if (isBaseCurrencyPegged(to))
        return forexToBaseCurrencyStr(amount, from);
    if (isBaseCurrencyPegged(from))
        return forexFromBaseCurrencyStr(amount, to);
    const price = forexRate(from, to);
    if (!price)
        return undefined;
    return formatCurrencyString(amount * price, to);
};
